import React, { useEffect, useState } from "react";
import { Bank, ArrowRight, Clock, CheckCircle, HourglassMedium } from "@phosphor-icons/react";
import { PageLoader, Stat, EmptyState } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../../components/ui/dialog";
import { toast } from "sonner";
import api, { money, apiErr } from "../../lib/api";

export default function Settlements() {
  const [s, setS] = useState(null);
  const [amount, setAmount] = useState("");
  const [open, setOpen] = useState(false);
  const load = () => api.get("/organizer/settlement").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);

  const request = async () => {
    try {
      const { data } = await api.post("/organizer/settlement/request", { amount: amount ? Number(amount) : null });
      toast.success(data.message); setOpen(false); setAmount(""); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!s) return <PageLoader />;

  return (
    <div className="max-w-4xl">
      <div className="overline mb-1">Ticket & event revenue</div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Settlement Balance</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" disabled={s.available <= 0} data-testid="request-settlement-btn">
              <Bank className="mr-1.5" /> Request settlement
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Request settlement</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">Available now: <b>{money(s.available)}</b> (events past their {s.hold_days}-day hold period). Leave blank to request the full available amount.</p>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Max ${money(s.available)}`} className="mt-3" data-testid="settlement-amount" />
            <DialogFooter><Button onClick={request} className="w-full rounded-full" data-testid="confirm-settlement">Submit request</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <p className="text-xs text-muted-foreground mb-4">This balance is your net ticket revenue and is kept fully separate from your prepaid Services Wallet.</p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <Stat label="Net earned" value={money(s.net_total)} accent />
        <Stat label="Available" value={money(s.available)} />
        <Stat label="Requested" value={money(s.requested)} />
        <Stat label="Settled" value={money(s.settled)} />
        <Stat label="Pending" value={money(s.pending)} />
      </div>

      <div className="overline mb-3">Per-event revenue</div>
      {s.per_event.length === 0 ? (
        <EmptyState icon={Bank} title="No revenue yet" subtitle="Confirmed paid bookings will appear here." testid="no-revenue" />
      ) : (
        <div className="rounded-xl border border-border bg-card divide-y divide-border mb-8">
          {s.per_event.map((e) => (
            <div key={e.event_id} className="flex items-center justify-between p-4" data-testid={`stl-event-${e.event_id}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{e.event}</div>
                <div className="text-xs text-muted-foreground">Gross {money(e.gross)} · {e.eligible ? <span className="text-emerald-600">eligible</span> : `holds till ${e.eligible_on || "event ends"}`}</div>
              </div>
              <div className="text-right shrink-0"><div className="font-semibold">{money(e.net)}</div><div className="text-xs text-muted-foreground">net</div></div>
            </div>
          ))}
        </div>
      )}

      <div className="overline mb-3">Settlement history</div>
      {s.history.length === 0 ? <p className="text-sm text-muted-foreground">No settlement requests yet.</p> : (
        <div className="rounded-xl border border-border bg-card divide-y divide-border">
          {s.history.map((h) => {
            const Icon = h.status === "completed" ? CheckCircle : h.status === "processing" ? HourglassMedium : Clock;
            return (
              <div key={h.id} className="flex items-center justify-between p-4" data-testid={`settlement-${h.id}`}>
                <div className="flex items-center gap-3">
                  <Icon size={18} className={h.status === "completed" ? "text-emerald-600" : "text-amber-600"} />
                  <div><div className="font-semibold">{money(h.amount)}</div>
                    <div className="text-xs text-muted-foreground">{new Date(h.created_at).toLocaleDateString("en-IN")}{h.reference ? ` · Ref ${h.reference}` : ""}</div></div>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-muted capitalize">{h.status}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
